import type { dealerCodeDataResponse } from "@dealer-center/ddc-pages-dealer-codes-interface";
import { defaultDealerCodesPageSize } from "@dealer-center/ddc-pages-dealer-codes-interface";
import type { DealerCode } from "@dealer-center/ddc-api-account-interface";
import {
  getDealerCodeTypesUrlToUse,
  getDealerCodesByAccountIdUrl,
  getDealerCodesUrlToUse,
  getDeleteDealerCodeMappingUrl,
  getEditDealerCodeMappingUrl,
  postDealerCodeTypeToAccountUrl
} from "./helper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type {
  dealerCodeMappingEditInterface,
  dealerCodeToAccountMap
} from "@dealer-center/ddc-api-lambda-helper-css";

const queryKeyBase = "ddc-pages-dealer-codes";

export function useDealerCodesGetTypes() {
  return useQuery({
    queryKey: [queryKeyBase],
    queryFn: () => fetchDealerCodesGetTypes()
  });
}

export function useMapDealerCodeTypeToAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase] });
    },
    mutationFn: (config: dealerCodeToAccountMap) => {
      return fetchMapDealerCodeTypeToAccount(config);
    }
  });
}

export function useDealerCodesGet(dealerCodeToFind?: string) {
  return useQuery({
    queryKey: [queryKeyBase, dealerCodeToFind],
    enabled: !!dealerCodeToFind,
    queryFn: () => fetchDealerCodesGet(dealerCodeToFind || "")
  });
}

export function useDealerCodeMappingDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase] });
    },
    mutationFn: (dealerCodeId: string) => {
      return fetchDealerCodeMappingDelete(dealerCodeId);
    }
  });
}

export function useDealerCodeMappingEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase] });
    },
    mutationFn: (fetchMutation: dealerCodeMappingEditInterface) => {
      const response = fetchDealerCodeMappingEdit(fetchMutation);
      return response;
    }
  });
}

export function useDealerCodesByAccountIdGet(accountIdToFind?: string) {
  return useQuery({
    queryKey: [queryKeyBase, accountIdToFind],
    enabled: !!accountIdToFind,
    queryFn: () => fetchDealerCodesByIdGet(accountIdToFind || "")
  });
}

const fetchDealerCodesByIdGet = async (accountIdToFind: string) => {
  const url = getDealerCodesByAccountIdUrl(accountIdToFind);
  const result = await fetch(url);
  if (!result.ok) {
    throw new Error(`Failed to fetch dealer codes: ${result.statusText}`);
  }
  return (await result.json()) as DealerCode[];
};

const fetchDealerCodesGet = async (dealerCodeToFind: string) => {
  const url = getDealerCodesUrlToUse(
    dealerCodeToFind,
    defaultDealerCodesPageSize,
    0
  );
  const fetchResult = await fetch(url);
  if (!fetchResult.ok) {
    throw new Error(`Failed to fetch dealer codes: ${fetchResult.statusText}`);
  }
  const result = (await fetchResult.json()) as dealerCodeDataResponse;
  const resultsToReturn = result.dealerCodeData;
  if (result.totalElements > defaultDealerCodesPageSize) {
    const fetchPromises: Promise<Response>[] = [];
    for (let i = 1; i < result.totalPages; i++) {
      const url = getDealerCodesUrlToUse(
        dealerCodeToFind,
        defaultDealerCodesPageSize,
        i
      );
      fetchPromises.push(fetch(url));
    }
    const fetchResults = await Promise.all(fetchPromises);
    for (const fetchResult of fetchResults) {
      if (!fetchResult.ok) {
        throw new Error(
          `Failed to fetch dealer codes: ${fetchResult.statusText}`
        );
      }
      const result = (await fetchResult.json()) as dealerCodeDataResponse;
      resultsToReturn.push(...result.dealerCodeData);
    }
  }
  return resultsToReturn;
};

const fetchDealerCodesGetTypes = async () => {
  const url = getDealerCodeTypesUrlToUse();
  const fetchResult = await fetch(url);
  if (!fetchResult.ok) {
    throw new Error(
      `Failed to fetch dealer code types: ${fetchResult.statusText}`
    );
  }
  return (await fetchResult.json()) as string[];
};

const fetchMapDealerCodeTypeToAccount = (
  config: dealerCodeToAccountMap
): Promise<string> => {
  return axios
    .post(postDealerCodeTypeToAccountUrl(), config)
    .then(res => res.data);
};

const fetchDealerCodeMappingDelete = (
  dealerCodeId: string
): Promise<string> => {
  return axios
    .post(getDeleteDealerCodeMappingUrl(dealerCodeId))
    .then(res => res.data);
};

const fetchDealerCodeMappingEdit = (
  fetchMutation: dealerCodeMappingEditInterface
): Promise<string> => {
  return axios
    .put(getEditDealerCodeMappingUrl(fetchMutation.id), fetchMutation)
    .catch(err => {
      throw new Error(
        `Failed to fetch dealer code types: ${err.response.statusText}`
      );
    })
    .then(res => res.data);
};
